import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiClock, FiTag, FiSearch, FiFilter, FiChevronRight, FiX, FiInfo } from 'react-icons/fi';
import DashboardLayout from '../../components/DashboardLayout';
import API from '../../utils/api';
import { getImageUrl, getPlaceholderUrl } from '../../utils/imageUrl';

const CustomerServices = () => {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [selectedService, setSelectedService] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const response = await API.get('/api/service');
        setServices(response.data);
        
        // Extract unique categories
        const uniqueCategories = [...new Set(response.data.map(service => service.category))];
        setCategories(uniqueCategories);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching services:', err);
        setError('Gagal memuat daftar layanan. Silakan coba lagi nanti.');
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    // Search is already handled by the filtered services
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
  };

  const openDetailModal = (service) => {
    setSelectedService(service);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
  };

  // Filter and sort services
  const filteredServices = services
    .filter(service => {
      // Filter by search query
      const matchesSearch = service.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                           service.description.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Filter by category
      const matchesCategory = selectedCategory === 'all' || service.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      // Sort by selected criteria
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'price_asc':
          return a.price - b.price;
        case 'price_desc':
          return b.price - a.price;
        case 'duration':
          return a.estimated_time - b.estimated_time;
        default:
          return 0;
      }
    });

  const formatPrice = (price) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <DashboardLayout userRole="customer" userName="Pelanggan">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Katalog Layanan</h1>
        <p className="text-gray-600">Jelajahi layanan bengkel kami untuk kendaraan Anda</p>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          {/* Search */}
          <div className="w-full md:w-64">
            <form onSubmit={handleSearch}>
              <label htmlFor="search" className="sr-only">Search</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiSearch className="text-gray-400" />
                </div>
                <input
                  type="text"
                  id="search"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary block w-full pl-10 p-2.5"
                  placeholder="Cari layanan..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </form>
          </div>
          
          {/* Filters */}
          <div className="flex space-x-4">
            <div>
              <label htmlFor="sort" className="block text-sm font-medium text-gray-700 mb-1">
                Urutkan
              </label>
              <select
                id="sort"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary block w-full p-2.5"
                value={sortBy}
                onChange={handleSortChange}
              >
                <option value="name">Nama (A-Z)</option>
                <option value="price_asc">Harga (Terendah)</option>
                <option value="price_desc">Harga (Tertinggi)</option>
                <option value="duration">Durasi</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Category Pills */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => handleCategoryChange('all')}
          className={`px-4 py-2 rounded-full text-sm font-medium ${
            selectedCategory === 'all'
              ? 'bg-primary text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Semua
        </button>
        
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => handleCategoryChange(category)}
            className={`px-4 py-2 rounded-full text-sm font-medium ${
              selectedCategory === category
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {category}
          </button>
        ))}
      </div>

      {/* Services List */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : error ? (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      ) : filteredServices.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <div className="inline-block p-4 bg-gray-100 rounded-full mb-4">
            <FiFilter className="text-gray-500" size={30} />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Tidak ada layanan ditemukan</h3>
          <p className="text-gray-600">
            Coba ubah filter atau kata kunci pencarian Anda
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredServices.map((service) => (
            <div key={service.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
              <div className="relative h-48">
                <img 
                  className="w-full h-48 object-cover"
                  src={service.image_url ? getImageUrl(service.image_url) : getPlaceholderUrl('Layanan+Bengkel', 400, 200)}
                  alt={service.name}
                  onError={(e) => {
                    console.error(`Failed to load image for service ${service.id}:`, service.image_url);
                    e.target.onerror = null;
                    e.target.src = getPlaceholderUrl('No+Image');
                  }}
                />
                <div className="absolute top-0 right-0 bg-primary text-white px-3 py-1 m-2 rounded-full text-sm font-medium">
                  {formatPrice(service.price)}
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center mb-2">
                  <FiTag className="text-primary mr-2" />
                  <span className="text-sm text-gray-500">{service.category}</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{service.name}</h3>
                <p className="text-gray-600 mb-4 line-clamp-3">{service.description}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-gray-500">
                    <FiClock className="mr-1" />
                    <span className="text-sm">{service.estimated_time} menit</span>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => openDetailModal(service)}
                      className="flex items-center text-blue-600 hover:text-blue-800"
                    >
                      <FiInfo className="mr-1" /> Detail
                    </button>
                    <Link
                      to={`/customer/book?service=${service.id}`}
                      className="flex items-center text-primary hover:text-primary-dark"
                    >
                      Booking <FiChevronRight className="ml-1" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedService && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-800">Detail Layanan</h2>
              <button 
                onClick={closeDetailModal} 
                className="text-gray-500 hover:text-gray-700"
              >
                <FiX size={24} />
              </button>
            </div>
            <div className="p-6">
              <div className="relative h-64 mb-6">
                <img
                  className="w-full h-64 object-cover rounded-lg"
                  src={selectedService.image_url ? getImageUrl(selectedService.image_url) : getPlaceholderUrl('Layanan+Bengkel', 600, 300)}
                  alt={selectedService.name}
                  onError={(e) => {
                    console.error(`Failed to load image for service ${selectedService.id}:`, selectedService.image_url);
                    e.target.onerror = null;
                    e.target.src = getPlaceholderUrl('No+Image');
                  }}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">{selectedService.name}</h3>
                  <div className="flex items-center mb-4">
                    <FiTag className="text-primary mr-2" />
                    <span className="text-gray-600">{selectedService.category}</span>
                  </div>
                  <div className="flex items-center mb-4">
                    <FiClock className="text-primary mr-2" />
                    <span className="text-gray-600">Estimasi Waktu: {selectedService.estimated_time} menit</span>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 mb-4">
                    <p className="text-lg font-semibold text-green-700">{formatPrice(selectedService.price)}</p>
                  </div>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">Deskripsi Layanan</h4>
                  <p className="text-gray-600 mb-4">{selectedService.description}</p>
                </div>
              </div>
              <div className="border-t pt-6 flex justify-end">
                <Link
                  to={`/customer/book?service=${selectedService.id}`}
                  className="bg-primary text-white py-2 px-6 rounded-lg hover:bg-primary-dark transition-colors"
                >
                  Booking Sekarang
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default CustomerServices; 